import type { ReactElement } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/state/AuthContext'
import { isViewer } from '@/components/auth/isViewer'
import { useMyInvitations } from '@/lib/invites/useMyInvitations'

/** Signed-in pages. Viewers (no organisation and no event they manage) only reach those marked viewerAllowed; from any
 *  other they go to start hosting, which brings them back here once they have an organisation. Plain `/app` is where
 *  signing in lands when there's nowhere else to go (the header's Log in, the emailed link), so a viewer there goes
 *  home, unless a manager invitation waits for them (docs/features/event-managers.md), which `/app` shows: only a
 *  host page with more to it (`/app?create=1`, an event) says they mean to host.
 *  A page open to viewers stays on screen while a new token's account reloads (starting hosting applies one, and the
 *  page shows the new handle meanwhile); only the first load waits. A host page waits, since an account that is
 *  still a viewer's would send them to start hosting. */
export function PrivateRoute({ children, viewerAllowed = false }: { children: ReactElement; viewerAllowed?: boolean }) {
  const { isAuthenticated, authReady, organization, user } = useAuth()
  const location = useLocation()
  if (isAuthenticated && !authReady && !(viewerAllowed && user)) return null
  if (!isAuthenticated) return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`} replace />
  if (!viewerAllowed && isViewer(organization, user)) {
    if (location.pathname.replace(/\/+$/, '') === '/app' && !location.search) return <ViewerLanding>{children}</ViewerLanding>
    const returnTo = location.pathname + location.search + location.hash
    return <Navigate to={`/start-hosting?${new URLSearchParams({ return_to: returnTo })}`} replace />
  }
  return children
}

/** A viewer who has only signed in: their invitations, if any wait for them, else home. Nothing shows while it asks. */
function ViewerLanding({ children }: { children: ReactElement }) {
  const { invitations, loading } = useMyInvitations()
  if (loading) return null
  return invitations.length ? children : <Navigate to="/" replace />
}
